<script lang="ts" setup>
import { GetPetById } from '@/api/petApi'
import type { PetDeathRecord } from '@/api/mortalityApi'
import { resolveFrappeFileUrl } from '@/services/frappe/files'
import { AnimalGender, AnimalSpecies, AnimalType, BloodType, Pet, PetStatus } from '@/type/pet-profile'
import type { Component } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import PetPanelSkeleton from '@/components/pet-profile/PetPanelSkeleton.vue'
import { resolvePetPhotoPath } from '@/utils/petPhoto'

definePage({
  meta: {
    navActiveLink: 'pet-profile-profile',
  },
})

type PetProfileTab
  = 'profile'
    | 'dashboard'
    | 'insights'
    | 'medical-profile'
    | 'guardians'
    /**
     * ⚠️ THE `vaccination` TAB NOW RENDERS `Preventive Care Record`.
     *
     * Contract §8: the frontend must stop reading `Pet Vaccination Record` /
     * `Pet Deworming Record` (both deleted) and stop using `PetCareService` for this work. The
     * tab keeps its route, its label and its design; only the model beneath it changed.
     *
     * The second `preventive-care` tab added while the backend was still in flight is gone —
     * two tabs showing the same thing was the duplication, not the fix. The old component
     * (`views/pages/pet-profile/car-service/index.vue`) is left on disk untouched, so
     * reverting is a one-line change here.
     *
     * ⚠️ This does NOT touch the `PetCareService` path itself: grooming, bathing and boarding
     * run through the healthcare workspaces, not through this tab, and none of their files
     * were modified.
     */
    | 'vaccination'
    | 'health-information'

function petAsyncComponent(loader: () => Promise<{ default: Component }>) {
  return defineAsyncComponent({
    loader: () => loader().then(module => module.default),
    suspensible: false,
  })
}

/**
 * A tab panel's chunk downloads on first visit. `suspensible: false` means `Suspense` will NOT
 * catch that wait, so the panel owns its own placeholder — without it the region goes blank and
 * the page height collapses mid-switch. `delay: 0` shows it immediately; a cached chunk resolves
 * in the same tick and the skeleton never paints.
 */
function petPanelComponent(loader: () => Promise<{ default: Component }>) {
  return defineAsyncComponent({
    loader: () => loader().then(module => module.default),
    suspensible: false,
    loadingComponent: PetPanelSkeleton,
    delay: 0,
  })
}

const PetProfileHeader = petAsyncComponent(() => import('@/components/pet-profile/PetProfileHeader.vue'))
const PetProfileMobileHero = petAsyncComponent(() => import('@/components/pet-profile/mobile/PetProfileMobileHero.vue'))
const PetProfileMobileActions = petAsyncComponent(() => import('@/components/pet-profile/mobile/PetProfileMobileActions.vue'))
const PetProfileMobileTabs = petAsyncComponent(() => import('@/components/pet-profile/mobile/PetProfileMobileTabs.vue'))
const DeathReportDialog = petAsyncComponent(() => import('@/components/graveyard/DeathReportDialog.vue'))
const QRGeneratorDialog = petAsyncComponent(() => import('@/components/pet-profile/QRGeneratorDialog.vue'))
const ImageIsDefaultDialog = petAsyncComponent(() => import('@/components/pet-profile/imageIsDefaultDialog.vue'))
const Profile = petPanelComponent(() => import('@/views/pages/pet-profile/profile/index.vue'))
const Guardians = petPanelComponent(() => import('@/views/pages/pet-profile/guardians/index.vue'))
const Vaccination = petPanelComponent(() => import('@/views/pages/pet-profile/preventive-care/index.vue'))
const HealthInformation = petPanelComponent(() => import('@/views/pages/pet-profile/health-information/index.vue'))
const MedicalProfile = petPanelComponent(() => import('@/components/pet-profile/medical-profile/PetMedicalProfileDashboard.vue'))
const PetDashboard = petPanelComponent(() => import('@/components/pet-profile/dashboard/PetDashboardTab.vue'))
const PetInsights = petPanelComponent(() => import('@/components/pet-profile/insights/PetInsightsTab.vue'))
const PetAddDialog = petAsyncComponent(() => import('@/components/pet-profile/PetAddDialog.vue'))

const route = useRoute('pet-profile-id-tab')
const router = useRouter()
const { t } = useI18n()

const { smAndDown } = useDisplay()

const loading = ref(false)
const editDialog = ref(false)
const loadError = ref('')

// Owned here, not in the header, so the desktop header and the mobile hero share one set.
const qrDialog = ref(false)
const imageDialog = ref(false)
const deathDialog = ref(false)
let petRequestController: AbortController | null = null
let petRequestId = 0

function routeParam(value: unknown, fallback = '') {
  const raw = Array.isArray(value) ? value[0] : value

  return String(raw ?? fallback).trim()
}

function emptyPet(): Pet {
  return {
    name: '',
    pet_name: '',
    animal_species: AnimalSpecies.Mammal,
    animal_type: AnimalType.Dog,
    breed: '',
    birth_date: '',
    registration_date: '',
    pet_image: null,
    color: '',
    gender: AnimalGender.Male,
    weight: '',
    hight: '',
    blood_type: BloodType.APositive,
    activity_exercise: '',
    status: PetStatus.Available,
    food_type: '',
    type_name: '',
    brand_name: '',
    food_brand: '',
    images: [],
  }
}

const routePetId = computed(() => routeParam(route.params.id))
const routeTab = computed(() => routeParam(route.params.tab, 'profile'))
const validTabs = new Set<PetProfileTab>([
  'profile',
  'dashboard',
  'insights',
  'medical-profile',
  'guardians',
  'vaccination',
  'health-information',
])

const activeTab = computed<PetProfileTab>(() => {
  const tab = routeTab.value as PetProfileTab

  return validTabs.has(tab) ? tab : 'profile'
})

const tabs = computed(() => [
  { title: t('alkokh.pet.profile.tabs.profile'), icon: 'tabler-user-check', tab: 'profile' as const },
  { title: t('alkokh.pet.profile.tabs.dashboard'), icon: 'tabler-chart-histogram', tab: 'dashboard' as const },
  { title: t('alkokh.pet.profile.tabs.insights'), icon: 'tabler-bulb', tab: 'insights' as const },
  { title: t('alkokh.pet.profile.tabs.medicalProfile'), icon: 'tabler-heartbeat', tab: 'medical-profile' as const },
  { title: t('alkokh.pet.profile.tabs.guardians'), icon: 'tabler-users', tab: 'guardians' as const },
  { title: t('alkokh.pet.profile.tabs.vaccination'), icon: 'tabler-vaccine', tab: 'vaccination' as const },
  { title: t('alkokh.pet.profile.tabs.healthInformation'), icon: 'tabler-clipboard-heart', tab: 'health-information' as const },
])

const pet = ref<Pet>(emptyPet())
const hasPet = computed(() => Boolean(pet.value.name))

function abortPetRequest() {
  petRequestId += 1
  petRequestController?.abort()
  petRequestController = null
}

async function loadPet(id: string) {
  if (!id)
    return

  abortPetRequest()
  loadError.value = ''
  pet.value = emptyPet()

  const requestId = petRequestId
  const controller = new AbortController()
  petRequestController = controller

  const result = await GetPetById(loading, id, { signal: controller.signal })

  if (controller.signal.aborted || requestId !== petRequestId)
    return

  const nextPet = result?.message?.data

  if (nextPet) {
    pet.value = {
      ...emptyPet(),
      ...nextPet,
      images: nextPet.images ?? [],
    }

    return
  }

  loadError.value = t('alkokh.global.noData')
}

function goToTab(tab: PetProfileTab) {
  if (tab === activeTab.value)
    return

  router.push({
    name: 'pet-profile-id-tab',
    params: {
      id: routePetId.value,
      tab,
    },
  })
}

async function refreshPet() {
  await loadPet(routePetId.value)
}

// Was `?? catImage` in the header — every photo-less pet showed the SAME cat photograph. Returns
// undefined so the avatar falls through to the species icon (see docs/AGENT.md).
// ⚠️ NOT an inline `findLast(custom_is_default)`. Uploading never sets that flag, so the first
// photo added to a pet resolved to undefined here. See `resolvePetPhotoPath`.
const petImage = computed(() =>
  resolveFrappeFileUrl(resolvePetPhotoPath(pet.value.images)),
)

async function handleDeathRecordSaved(record: PetDeathRecord) {
  await router.push(`/graveyard/graves/${encodeURIComponent(record.name)}`)
}

watch([routePetId, routeTab], ([id, tab]) => {
  const normalizedTab = validTabs.has(tab as PetProfileTab) ? tab : 'profile'

  if (!id)
    return

  if (tab !== normalizedTab) {
    router.replace({
      name: 'pet-profile-id-tab',
      params: {
        id,
        tab: normalizedTab,
      },
    })

    return
  }
}, { immediate: true })

watch(routePetId, id => {
  if (!id)
    return

  void loadPet(id)
}, { immediate: true })

onBeforeRouteLeave(() => {
  abortPetRequest()
})

onBeforeUnmount(abortPetRequest)
</script>

<template>
  <section
    class="pet-profile-page"
    :data-pet-id="routePetId"
  >
    <VSkeletonLoader
      v-if="loading && !hasPet"
      type="image, article, actions"
      class="mb-4"
    />

    <VAlert
      v-else-if="loadError"
      type="warning"
      variant="tonal"
      class="mb-4"
    >
      {{ loadError }}
    </VAlert>

    <template v-else>
      <!--
        Two heroes, chosen by the parent rather than branched inside one component: the desktop
        cover-image header and the mobile purple hero share almost no markup, so a single file
        would hold two disjoint templates.
      -->
      <PetProfileHeader
        v-if="!smAndDown"
        :key="`header-${routePetId}`"
        :pet="pet"
        @edit="editDialog = true"
        @qr="qrDialog = true"
        @image="imageDialog = true"
        @report-death="deathDialog = true"
      />

      <template v-else>
        <PetProfileMobileHero
          :key="`mobile-header-${routePetId}`"
          :pet="pet"
          :image="petImage"
          @edit="editDialog = true"
          @qr="qrDialog = true"
          @image="imageDialog = true"
          @report-death="deathDialog = true"
        />

        <PetProfileMobileActions
          :pet="pet"
          @edit="editDialog = true"
          @qr="qrDialog = true"
          @report-death="deathDialog = true"
          @go-to-tab="goToTab"
        />
      </template>

      <div
        v-if="!smAndDown"
        class="pet-profile-page__tabs"
      >
        <VBtn
          v-for="item in tabs"
          :key="item.tab"
          :color="activeTab === item.tab ? 'primary' : 'secondary'"
          :variant="activeTab === item.tab ? 'flat' : 'tonal'"
          :prepend-icon="item.icon"
          class="text-none"
          @click="goToTab(item.tab)"
        >
          {{ item.title }}
        </VBtn>
      </div>

      <PetProfileMobileTabs
        v-else
        :tabs="tabs"
        :active-tab="activeTab"
        @select="goToTab"
      />

      <!--
        Panels swap IN PLACE. Keyed on the pet, not on `pet + tab` — switching tabs no longer tears
        the region down and rebuilds it, so there is no scroll jump and the hero never reloads.
        `KeepAlive` holds each visited panel, so returning to one is instant and the data it already
        fetched survives instead of being refetched. Switching pets still keys a clean rebuild.

        The first visit to a tab still downloads its chunk; each panel supplies its own
        `PetPanelSkeleton` as `loadingComponent` for that window (see `petPanelComponent` above).
      -->
      <div
        class="pet-profile-page__panel"
        :key="routePetId"
      >
        <KeepAlive>
          <Profile
            v-if="activeTab === 'profile'"
            :pet="pet"
          />

          <PetDashboard
            v-else-if="activeTab === 'dashboard'"
            :pet="pet"
            :pet-id="routePetId"
          />

          <PetInsights
            v-else-if="activeTab === 'insights'"
            :pet="pet"
            :pet-id="routePetId"
          />

          <MedicalProfile
            v-else-if="activeTab === 'medical-profile'"
            :pet="pet"
            :pet-id="routePetId"
          />

          <Guardians
            v-else-if="activeTab === 'guardians'"
            :pet-id="routePetId"
          />

          <Vaccination
            v-else-if="activeTab === 'vaccination'"
            :pet="pet"
          />

          <HealthInformation
            v-else-if="activeTab === 'health-information'"
            :pet="pet"
          />
        </KeepAlive>
      </div>
    </template>

    <PetAddDialog
      v-if="editDialog"
      v-model="editDialog"
      mode="edit"
      guardian-mode="none"
      :pet-id="routePetId"
      :initial-pet="pet"
      @saved="refreshPet"
    />

    <QRGeneratorDialog
      v-if="qrDialog"
      v-model="qrDialog"
      :pet="pet"
    />

    <ImageIsDefaultDialog
      v-if="imageDialog"
      v-model="imageDialog"
      :pet-id="pet.name as string"
      :images="pet.images"
    />

    <DeathReportDialog
      v-if="deathDialog"
      v-model="deathDialog"
      :pet-id="String(pet.name || '')"
      :pet-name="pet.pet_name"
      :pet-species="String(pet.animal_type || pet.animal_species || '')"
      :pet-image="pet.pet_image || petImage || null"
      :lock-pet="true"
      :lock-source="true"
      :title="t('alkokh.graveyard.report.title')"
      :subtitle="t('alkokh.graveyard.report.profileSubtitle')"
      @saved="handleDeathRecordSaved"
    />
  </section>
</template>

<style scoped>
.pet-profile-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pet-profile-page__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pet-profile-page__panel {
  min-block-size: 240px;
}
</style>
